const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isDomAvailable = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const isElementNode = (node) => {
  if (!isDomAvailable()) return false;
  return (
    node instanceof window.HTMLElement || node instanceof window.SVGElement
  );
};

const classCleanup = () => {
  if (!isDomAvailable()) return;
  document
    .querySelectorAll('.a11y-error, .a11y-error-container')
    .forEach((el) => {
      el.classList.remove('a11y-error');
      el.classList.remove('a11y-error-container');
    });
};

const isElementVisible = (element, visited = new WeakSet()) => {
  if (!isDomAvailable() || !isElementNode(element)) return false;
  if (element === document.body) return true;

  if (visited.has(element)) {
    return false;
  }
  visited.add(element);

  if (element.hasAttribute('hidden')) return false;
  if (element.getAttribute('aria-hidden') === 'true') return false;

  const style = window.getComputedStyle(element);
  if (!style) return false;
  if (style.display === 'none') return false;
  if (style.visibility === 'hidden' || style.visibility === 'collapse')
    return false;

  const rect = element.getBoundingClientRect();
  if (!rect || (rect.width === 0 && rect.height === 0)) {
    if (style.overflow === 'visible' && element.children.length > 0) {
      return Array.from(element.children).some(
        (child) => !visited.has(child) && isElementVisible(child, visited),
      );
    }
  }

  if (style.position === 'fixed') {
    return rect && (rect.width > 0 || rect.height > 0);
  }

  if (element.offsetParent === null) {
    const parent = element.parentElement;
    if (!parent) {
      return rect && (rect.width > 0 || rect.height > 0);
    }
    return isElementVisible(parent, visited);
  }

  return rect && (rect.width > 0 || rect.height > 0);
};

const findFirstVisibleAncestor = (element) => {
  if (!isDomAvailable()) return null;
  let current = element;
  while (current && current !== document.body) {
    if (isElementVisible(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const findImageElement = (element) => {
  if (!isElementNode(element)) return null;
  if (element.tagName === 'IMG') {
    return element;
  }
  if (typeof element.querySelector === 'function') {
    return element.querySelector('img');
  }
  return null;
};

const getImagePreview = (element) => {
  if (!isDomAvailable() || !element) return null;
  const img = findImageElement(element);
  if (!img) return null;

  const source = img.currentSrc || img.src;
  if (!source) return null;

  const altText = (img.getAttribute('alt') || '').trim();
  return {
    src: source,
    alt: altText || 'Imagem relacionada ao erro',
  };
};

const cssEscape = (value) => {
  if (typeof value !== 'string') return value;
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
};

const findClosestMatchingAncestor = (element, predicate) => {
  if (!isDomAvailable()) return null;
  let current = element;
  while (current && current !== document.body) {
    if (predicate(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const getFirstElementByIds = (ids) => {
  if (!isDomAvailable() || !ids || !ids.length) return null;
  for (const id of ids) {
    const candidateId = id.trim();
    if (!candidateId) continue;
    const element = document.getElementById(candidateId);
    if (element) {
      return element;
    }
  }
  return null;
};

const getElementDirectText = (element) => {
  if (!isElementNode(element)) return '';
  const pieces = [];
  element.childNodes.forEach((node) => {
    if (node.nodeType === 3) {
      const value = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (value) {
        pieces.push(value);
      }
    } else if (node.nodeType === 1) {
      const elementNode = node;
      if (elementNode.getAttribute) {
        const explicitLabel =
          elementNode.getAttribute('data-label') ||
          elementNode.getAttribute('aria-label');
        if (explicitLabel) {
          const value = explicitLabel.trim();
          if (value) {
            pieces.push(value);
            return;
          }
        }
      }
      if (elementNode.classList?.contains('name')) {
        const value = (elementNode.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();
        if (value) {
          pieces.push(value);
          return;
        }
      }
      if (elementNode.childNodes.length === 1) {
        const value = (elementNode.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();
        if (value) {
          pieces.push(value);
        }
      }
    }
  });

  return pieces.join(' ').trim();
};

const describeElement = (element) => {
  if (!isElementNode(element)) return '';

  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel.trim().slice(0, 120);
  }

  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy && isDomAvailable()) {
    const labels = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .map((node) => (node?.textContent || '').trim())
      .filter(Boolean);
    if (labels.length) {
      return labels.join(' ').slice(0, 120);
    }
  }

  const directText = getElementDirectText(element);
  if (directText) {
    return directText.slice(0, 120);
  }

  const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) {
    return text.slice(0, 120);
  }

  return element.tagName ? element.tagName.toLowerCase() : 'elemento';
};

const findVisibilityController = (element) => {
  if (!isDomAvailable()) return null;

  const labelledAncestor = findClosestMatchingAncestor(element, (node) =>
    Boolean(node?.getAttribute?.('aria-labelledby')),
  );
  if (labelledAncestor) {
    const labelledBy = labelledAncestor.getAttribute('aria-labelledby');
    if (labelledBy) {
      const ids = labelledBy.split(/\s+/).filter(Boolean);
      const controller = getFirstElementByIds(ids);
      if (controller && isElementVisible(controller)) {
        return {
          element: controller,
          label: describeElement(controller),
        };
      }
    }
  }

  const tabPanel = findClosestMatchingAncestor(element, (node) => {
    const role = node?.getAttribute?.('role');
    if (role && role.toLowerCase() === 'tabpanel') return true;
    return node?.classList?.contains('tab-panel');
  });
  if (tabPanel) {
    const panelId = tabPanel.id;
    if (panelId) {
      const escapedId = cssEscape(panelId);
      const selectorCandidates = [
        `[role="tab"][aria-controls="${escapedId}"]`,
        `[aria-controls="${escapedId}"][role="button"]`,
        `[data-target="#${escapedId}"]`,
        `[href="#${escapedId}"]`,
      ];
      for (const selector of selectorCandidates) {
        const control = document.querySelector(selector);
        if (control && isElementVisible(control)) {
          return {
            element: control,
            label: describeElement(control),
          };
        }
      }
    }

    const tabContent = findClosestMatchingAncestor(tabPanel, (node) =>
      node?.classList?.contains('tab-content'),
    );
    if (tabContent) {
      const panels = Array.from(
        tabContent.querySelectorAll('.tab-panel, [role="tabpanel"]'),
      );
      const panelIndex = panels.indexOf(tabPanel);
      if (panelIndex >= 0) {
        const tabContainer = findClosestMatchingAncestor(
          tabContent,
          (node) =>
            node?.classList?.contains('br-tab') ||
            node?.classList?.contains('tabs-block'),
        );
        const navRoot = tabContainer?.querySelector?.('.tab-nav');
        const tabItems = navRoot
          ? Array.from(navRoot.querySelectorAll('.tab-item'))
          : [];
        const tabButton = tabItems[panelIndex]?.querySelector?.(
          'button,[role="tab"],[data-tab-target],a',
        );
        if (tabButton) {
          return {
            element: tabButton,
            label: describeElement(tabButton),
          };
        }
      }
    }
  }

  return null;
};

const ensureErrorVisibility = (targetElement) => {
  if (!isDomAvailable() || !isElementNode(targetElement)) {
    return {
      highlightElement: null,
      usedFallback: false,
      fallbackLabel: null,
    };
  }

  const targetVisible = isElementVisible(targetElement);
  if (targetVisible) {
    return {
      highlightElement: targetElement,
      usedFallback: false,
      fallbackLabel: null,
    };
  }

  const controller = findVisibilityController(targetElement);
  if (controller) {
    return {
      highlightElement: controller.element,
      usedFallback: true,
      fallbackLabel: controller.label,
    };
  }

  const ancestor = findFirstVisibleAncestor(targetElement);
  if (ancestor) {
    return {
      highlightElement: ancestor,
      usedFallback: true,
      fallbackLabel: describeElement(ancestor),
    };
  }

  return {
    highlightElement: targetElement,
    usedFallback: true,
    fallbackLabel: describeElement(targetElement),
  };
};

const scrollToElement = (element, { highlightClass = 'a11y-error' } = {}) => {
  classCleanup();
  if (!element || typeof element.scrollIntoView !== 'function') {
    return;
  }
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  });
  element.classList.add(highlightClass);
};

export {
  classCleanup,
  cssEscape,
  describeElement,
  ensureErrorVisibility,
  findClosestMatchingAncestor,
  findFirstVisibleAncestor,
  findImageElement,
  findVisibilityController,
  getElementDirectText,
  getFirstElementByIds,
  getImagePreview,
  isDomAvailable,
  isElementNode,
  isElementVisible,
  scrollToElement,
  sleep,
};
